import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2 } from "lucide-react";

const TYPES = [
  { value: "data_export", label: "Get a copy of my data" },
  { value: "data_deletion", label: "Delete my data" },
  { value: "correction", label: "Correct my data" },
  { value: "opt_out", label: "Opt out of communications" },
];

export default function PrivacyRequest() {
  const [form, setForm] = useState({ requester_name: "", email: "", relationship: "", request_type: "", details: "" });
  const [hp, setHp] = useState("");
  const [formTs] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.requester_name.trim() || !form.email.trim()) return toast.error("Please share your name and email.");
    if (!form.request_type) return toast.error("Please choose a request type.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/privacy-request", { ...form, company_website: hp, _form_ts: formTs });
      setDone(data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-5 py-14" data-testid="privacy-request-page">
      <div className="flex items-center gap-2 mb-2 text-[#9C1E22]"><ShieldCheck size={20} /><span className="text-sm font-medium uppercase tracking-wide">Privacy</span></div>
      <h1 className="font-display text-3xl font-bold text-[#252221] mb-2">Privacy Request</h1>
      <p className="text-[14px] text-[#6b7480] mb-6">Request a copy of your data, a correction, deletion, or to opt out of communications. We verify identity before acting on any request. Deletion requests are reviewed by our team — nothing is deleted automatically.</p>

      <Card className="p-6 border-[#E6EDF2]">
        {done ? (
          <div className="text-center py-3" data-testid="privacy-request-success">
            <CheckCircle2 size={40} className="mx-auto mb-3 text-[#2F7D5A]" />
            <h2 className="font-display text-lg font-semibold text-[#252221] mb-1">Request received</h2>
            <p className="text-sm text-muted-foreground">Reference <span className="font-mono">{done.reference}</span>. {done.message}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)}
              aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <div>
              <Label className="text-[13px]">Your name</Label>
              <Input data-testid="pr-name" value={form.requester_name} onChange={(e) => set("requester_name", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[13px]">Email</Label>
              <Input data-testid="pr-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[13px]">Relationship to the record (optional)</Label>
              <Input data-testid="pr-relationship" value={form.relationship} onChange={(e) => set("relationship", e.target.value)} placeholder="e.g. myself, daughter, power of attorney" className="mt-1" />
            </div>
            <div>
              <Label className="text-[13px]">Request type</Label>
              <Select value={form.request_type} onValueChange={(v) => set("request_type", v)}>
                <SelectTrigger className="mt-1" data-testid="pr-type"><SelectValue placeholder="Choose a request type" /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`pr-type-${t.value}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[13px]">Details (optional)</Label>
              <Textarea data-testid="pr-details" value={form.details} onChange={(e) => set("details", e.target.value)} rows={3} className="mt-1" placeholder="Anything that helps us locate your record." />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#9C1E22] hover:bg-[#831a1d]" data-testid="pr-submit">
              {submitting ? "Sending…" : "Submit request"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">See our <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
          </form>
        )}
      </Card>
    </div>
  );
}
